/**
 * Schematische Gerätebilder für den Einkauf.
 *
 * Bewusst keine Herstellerfotos: Die Bilder werden aus den Katalogdaten
 * gezeichnet – Bauart je Gerätekategorie, Höhe je Höheneinheit, Farbe je
 * Herstellerklasse. So sieht jedes Modell anders aus, ohne dass jemand fremde
 * Fotos einbinden muss.
 */
import type { CSSProperties } from 'react';

export type ArtKind = 'server' | 'router' | 'switch' | 'firewall' | 'loadbalancer' | 'ups';

// Nüchterne Grautöne: die Herstellerklasse zeigt sich in der Helligkeit, nicht in Farbe.
const TIER_FILL: Record<string, { body: string; accent: string }> = {
  budget: { body: '#6b7280', accent: '#d1d5db' },
  standard: { body: '#4b5563', accent: '#e5e7eb' },
  enterprise: { body: '#374151', accent: '#f3f4f6' },
};

const LED_OK = '#22c55e';
const LED_WARN = '#f59e0b';

interface Props {
  kind: ArtKind;
  heightUnits: number;
  tier?: string;
  className?: string;
  style?: CSSProperties;
}

/** Höhe des Gehäuses im Bild: eine Höheneinheit ist 16 Pixel, mindestens 28. */
function chassisHeight(kind: ArtKind, heightUnits: number): number {
  if (kind === 'ups') return 64;
  return Math.max(28, Math.min(heightUnits, 8) * 16);
}

export function DeviceArt({ kind, heightUnits, tier = 'standard', className, style }: Props) {
  const palette = TIER_FILL[tier] ?? TIER_FILL.standard;
  const h = chassisHeight(kind, heightUnits);
  const width = 240;
  const top = (120 - h) / 2;
  const label = `${kind}, ${heightUnits} HE`;

  return (
    <svg
      viewBox="0 0 240 120"
      role="img"
      aria-label={label}
      className={className}
      style={style}
    >
      {/* Schatten am Boden */}
      <ellipse cx="120" cy="108" rx="96" ry="5" fill="#000" opacity="0.18" />

      {kind === 'server' && <Server top={top} h={h} width={width} palette={palette} tier={tier} kind={kind} />}
      {kind === 'router' && <Router top={top} h={h} width={width} palette={palette} />}
      {kind === 'switch' && <Switch top={top} h={h} width={width} palette={palette} />}
      {kind === 'firewall' && <Firewall top={top} h={h} width={width} palette={palette} />}
      {kind === 'loadbalancer' && <LoadBalancer top={top} h={h} width={width} palette={palette} />}
      {kind === 'ups' && <Ups top={top} h={h} width={width} palette={palette} />}
    </svg>
  );
}

interface PartProps {
  top: number;
  h: number;
  width: number;
  palette: { body: string; accent: string };
  kind?: ArtKind;
  tier?: string;
}

function Body({ top, h, width, palette, tier = 'standard', kind = 'server' }: PartProps) {
  const x = (240 - width) / 2;
  return (
    <>
      <rect x={x} y={top} width={width} height={h} rx="6" fill={palette.body} />
      <rect x={x} y={top} width={width} height={h} rx="6" fill="none" stroke={palette.accent} strokeOpacity="0.5" />
    </>
  );
}

function Led({ cx, cy, ok = true }: { cx: number; cy: number; ok?: boolean }) {
  return <circle cx={cx} cy={cy} r="2.6" fill={ok ? LED_OK : LED_WARN} />;
}

function Server({ top, h, width, palette, tier, kind }: PartProps) {
  const x = (240 - width) / 2;
  const bays = Math.max(4, Math.round(width / 26));
  const bayW = (width - 40) / bays;
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier={tier} kind={kind} />
      {/* Laufwerksschächte an der Front */}
      {Array.from({ length: bays }).map((_, i) => (
        <rect
          key={i}
          x={x + 14 + i * bayW + 2}
          y={top + h * 0.22}
          width={bayW - 6}
          height={h * 0.56}
          rx="2"
          fill={palette.accent}
          opacity="0.35"
        />
      ))}
      {/* Status-LEDs rechts */}
      <Led cx={x + width - 16} cy={top + h * 0.35} />
      <Led cx={x + width - 16} cy={top + h * 0.6} ok={h > 40} />
      <Led cx={x + width - 16} cy={top + h * 0.85} ok />
    </>
  );
}

function Router({ top, h, width, palette }: PartProps) {
  const x = (240 - width) / 2;
  const ports = 12;
  const pw = (width - 40) / ports;
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier="standard" kind="router" />
      {Array.from({ length: ports }).map((_, i) => (
        <rect key={i} x={x + 20 + i * pw} y={top + h * 0.55} width={pw - 4} height={h * 0.25} rx="1.5" fill={palette.accent} opacity="0.7" />
      ))}
      <Led cx={x + 18} cy={top + h * 0.3} />
      <Led cx={x + 30} cy={top + h * 0.3} />
    </>
  );
}

function Switch({ top, h, width, palette }: PartProps) {
  const x = (240 - width) / 2;
  const cols = 12;
  const cw = (width - 40) / cols;
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier="standard" kind="switch" />
      {Array.from({ length: cols }).map((_, i) => (
        <g key={i}>
          <rect x={x + 20 + i * cw} y={top + h * 0.3} width={cw - 4} height={h * 0.2} rx="1" fill={palette.accent} opacity="0.7" />
          <rect x={x + 20 + i * cw} y={top + h * 0.56} width={cw - 4} height={h * 0.2} rx="1" fill={palette.accent} opacity="0.7" />
        </g>
      ))}
      <Led cx={x + width - 14} cy={top + h * 0.5} />
    </>
  );
}

function Firewall({ top, h, width, palette }: PartProps) {
  const cx = 120;
  const cy = top + h / 2;
  const s = Math.min(h * 0.6, 26);
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier="enterprise" kind="firewall" />
      {/* Schild-Symbol */}
      <path
        d={`M ${cx} ${cy - s / 2} L ${cx + s / 2.2} ${cy - s / 4} L ${cx + s / 2.2} ${cy + s / 8} Q ${cx + s / 2.2} ${cy + s / 2} ${cx} ${cy + s / 2} Q ${cx - s / 2.2} ${cy + s / 2} ${cx - s / 2.2} ${cy + s / 8} L ${cx - s / 2.2} ${cy - s / 4} Z`}
        fill={palette.accent}
        opacity="0.8"
      />
      <Led cx={24} cy={top + h / 2} />
      <Led cx={216} cy={top + h / 2} />
    </>
  );
}

function LoadBalancer({ top, h, width, palette }: PartProps) {
  const x = (240 - width) / 2;
  const mid = top + h / 2;
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier="standard" kind="loadbalancer" />
      {/* Ein Eingang, zwei Ausgänge */}
      <path d={`M ${x + 20} ${mid} L ${x + width / 2} ${mid}`} stroke={palette.accent} strokeWidth="3" />
      <path d={`M ${x + width / 2} ${mid} L ${x + width - 40} ${top + h * 0.3} M ${x + width / 2} ${mid} L ${x + width - 40} ${top + h * 0.7}`} stroke={palette.accent} strokeWidth="3" />
      <Led cx={x + width - 18} cy={top + h * 0.3} />
      <Led cx={x + width - 18} cy={top + h * 0.7} />
    </>
  );
}

function Ups({ top, h, width, palette }: PartProps) {
  const x = (240 - width) / 2;
  const cells = 8;
  const cw = (width - 40) / cells;
  return (
    <>
      <Body top={top} h={h} width={width} palette={palette} tier="standard" kind="ups" />
      {Array.from({ length: cells }).map((_, i) => (
        <rect key={i} x={x + 20 + i * cw} y={top + h * 0.3} width={cw - 6} height={h * 0.4} rx="2" fill={palette.accent} opacity="0.65" />
      ))}
      <Led cx={x + width - 14} cy={top + h * 0.15} />
    </>
  );
}
